/**
 * TimeFieldButtonWithBanner - Time field button with timezone banner
 *
 * A wrapper component that renders the TimeFieldButton along with a TimezoneBanner
 * when there's a timezone difference between the device and the job site.
 */

import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useDateTime} from '@hconnect/tuc-processes/fields/dateTime/useDateTime'
import {Box} from '@mui/material'
import React from 'react'

import {TimezoneBanner} from '../../../../../orderManagement/components/TimezoneBanner'

import {TimeFieldButton} from './TimeFieldButton'

interface TimeFieldButtonWithBannerProps {
  onClick: () => void
  requiresApproval?: boolean
  isReadOnly?: boolean
  isChanged?: boolean
}

/**
 * TimeFieldButtonWithBanner Component
 *
 * Renders the time field button with an optional timezone banner underneath.
 * The banner is shown when there's a timezone difference between the user's device
 * and the job site.
 *
 * @example
 * ```tsx
 * <TimeFieldButtonWithBanner
 *   onClick={() => handleFieldClick('time')}
 *   isReadOnly={false}
 *   isChanged={true}
 *   requiresApproval={false}
 * />
 * ```
 */
export const TimeFieldButtonWithBanner: React.FC<TimeFieldButtonWithBannerProps> = ({
  onClick,
  requiresApproval = false,
  isReadOnly = false,
  isChanged = false
}) => {
  const {dateTime} = useDateTime()
  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')
  const jobSiteTimezone = dateTimeConfiguration?.businessHours.timeZone

  // Get the selected date from the form
  const selectedDate = dateTime?.[0] ?? null

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <TimeFieldButton
        onClick={onClick}
        requiresApproval={requiresApproval}
        isReadOnly={isReadOnly}
        isChanged={isChanged}
      />
      <TimezoneBanner
        jobSiteTimezone={jobSiteTimezone}
        selectedDate={selectedDate}
        data-test-id="time-field-timezone-banner"
      />
    </Box>
  )
}
